import type { ApolloCache, Reference, StoreObject } from '@apollo/client'

import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/gql'
import type {
  CommentData,
  UndismissCommentMutation,
} from '~publish/gql/graphql'
import { isOfType } from '~publish/helpers/typeGuards'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'

import type { FilterStatus } from '../CommentsFilters/types'
import { useCommentStatus } from '../CommentsFilters/useFilters'

export const UndismissCommentMutationGQL = graphql(/* GraphQL */ `
  mutation UndismissComment($input: UndismissCommentInput!) {
    undismissComment(input: $input) {
      __typename
      ... on UndismissCommentSuccess {
        comment {
          ... on BaseComment {
            __typename
            id
            status
            text
            updatedAt
            parentId
          }
          ... on CommentFromThirdParty {
            labels
          }
          ... on CommentFromBuffer {
            publishingStatus {
              status
              errorMessage
            }
          }
        }
      }
      ... on NotFoundError {
        message
      }
      ... on UnauthorizedError {
        message
      }
      ... on UnexpectedError {
        message
      }
    }
  }
`)

export const updateCacheAfterDismiss = (
  cache: ApolloCache<unknown>,
  data: UndismissCommentMutation | null | undefined,
  status: FilterStatus,
): void => {
  const result = data?.undismissComment
  if (!isOfType(result, 'UndismissCommentSuccess')) {
    return
  }

  const updatedComment = result.comment
  if (!updatedComment) return

  // 1. Update the comment in the cache with the new status
  cache.modify({
    id: cache.identify({
      __typename: updatedComment.__typename,
      id: updatedComment.id,
    }),
    fields: {
      status: () => updatedComment.status,
    },
  })

  // 2. Decide if the comment should be removed from the current view
  // For undismiss, we remove from the list if we're in the "dismissed" view
  const shouldRemoveFromList = status === 'dismissed'

  if (shouldRemoveFromList) {
    // Find all comments queries in the cache and update them
    cache.modify({
      fields: {
        comments: (
          existingCommentsRef: Reference | StoreObject,
          { readField },
        ) => {
          const edges =
            readField<{ node: CommentData }[]>('edges', existingCommentsRef) ||
            []

          // Filter out the comment from the edges array
          const newEdges = edges.filter((edge: { node: CommentData }) => {
            const commentId = readField('id', readField('node', edge))
            return commentId !== updatedComment.id
          })

          // Return the new list without the undismissed comment
          return {
            ...existingCommentsRef,
            edges: newEdges,
          }
        },
      },
    })
  }
}

type UseUndismissCommentResult = {
  undismissComment: (commentId: string) => Promise<void>
  loading: boolean
}

export function useUndismissComment(): UseUndismissCommentResult {
  const status = useCommentStatus()

  const [mutate, { loading }] = useTypedMutation(
    UndismissCommentMutationGQL,
    (data) => data.undismissComment,
    {
      successTypename: 'UndismissCommentSuccess',
      update: (cache, { data }) => {
        updateCacheAfterDismiss(cache, data, status)
      },
    },
  )

  const undismissComment = async (commentId: string): Promise<void> => {
    try {
      const result = await mutate({
        variables: {
          input: {
            commentId,
          },
        },
      })

      if (result.success) {
        toast.success('Comment reopened successfully', {
          duration: 1_000,
        })
      } else {
        // eslint-disable-next-line no-console
        console.error('Error reopening comment:', result.error)
        toast.error('An error occurred while reopening the comment')
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error reopening comment:', error)
      toast.error('An error occurred while reopening the comment')
    }
  }

  return {
    undismissComment,
    loading,
  }
}
